import { CALENDAR_API_ITEM_TYPES } from '@/app/(main)/calendar/consts/calendar-api';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import type {
  BirthdayCalendarItemApi,
  CalendarItemApi,
  CalendarTaskItemApi,
  CalendarTaskReminderItemApi,
  ExternalEventCalendarItemApi,
} from '@/types/calendarApiTypes';
import type { CalendarEvent } from '@/types/calendarTypes';

function getTaskClientId(related: { client: { id: number } | null }): string | undefined {
  return related.client ? String(related.client.id) : undefined;
}

function mapBirthdayItem(item: BirthdayCalendarItemApi): CalendarEvent {
  return {
    id: item.id,
    type: CALENDAR_EVENT_TYPES.BIRTHDAY,
    title: item.entity.name,
    start: item.date,
    allDay: item.all_day,
    source: 'birthday',
    clientId: String(item.entity.id),
  };
}

function getTaskDeadlineStart(item: CalendarTaskItemApi, isOverdue: boolean): string {
  if (isOverdue && item.metadata.original_deadline) {
    return item.metadata.original_deadline;
  }

  return item.date;
}

function mapTaskItem(item: CalendarTaskItemApi): CalendarEvent {
  const status = item.entity.status;
  const isOverdue = item.type === CALENDAR_API_ITEM_TYPES.TASK_OVERDUE || item.metadata.is_overdue;

  return {
    id: item.id,
    type: CALENDAR_EVENT_TYPES.TASK_DEADLINE,
    title: item.title,
    start: getTaskDeadlineStart(item, isOverdue),
    allDay: item.all_day,
    source: 'task',
    taskId: String(item.entity.id),
    clientId: getTaskClientId(item.related),
    status,
    isOverdue,
  };
}

function mapTaskReminderItem(item: CalendarTaskReminderItemApi): CalendarEvent {
  return {
    id: item.id,
    type: CALENDAR_EVENT_TYPES.TASK_REMINDER,
    title: item.title,
    start: item.all_day ? item.date : (item.start_at ?? item.date),
    end: item.end_at ?? undefined,
    allDay: item.all_day,
    source: 'task',
    taskId: String(item.entity.id),
    clientId: getTaskClientId(item.related),
    status: item.entity.status,
  };
}

function mapExternalEventItem(item: ExternalEventCalendarItemApi): CalendarEvent {
  const isInPerson = item.entity.in_person;
  const location = item.entity.location ?? item.metadata.location ?? undefined;

  return {
    id: item.id,
    type: isInPerson ? CALENDAR_EVENT_TYPES.OUTLOOK_IN_PERSON : CALENDAR_EVENT_TYPES.OUTLOOK_VIRTUAL,
    title: item.title,
    start: item.all_day ? item.date : (item.start_at ?? item.date),
    end: item.end_at ?? undefined,
    allDay: item.all_day,
    source: 'outlook',
    outlookEventId: item.metadata.external_event_id ?? item.entity.id,
    description: item.metadata.description ?? undefined,
    externalUrl: item.entity.event_url ?? undefined,
    address: location,
  };
}

function isBirthdayItem(item: CalendarItemApi): item is BirthdayCalendarItemApi {
  return item.type === CALENDAR_API_ITEM_TYPES.BIRTHDAY;
}

function isTaskItem(item: CalendarItemApi): item is CalendarTaskItemApi {
  return item.type === CALENDAR_API_ITEM_TYPES.TASK_DUE || item.type === CALENDAR_API_ITEM_TYPES.TASK_OVERDUE;
}

function isTaskReminderItem(item: CalendarItemApi): item is CalendarTaskReminderItemApi {
  return item.type === CALENDAR_API_ITEM_TYPES.TASK_REMINDER;
}

function isExternalEventItem(item: CalendarItemApi): item is ExternalEventCalendarItemApi {
  return item.type === CALENDAR_API_ITEM_TYPES.EXTERNAL_EVENT;
}

export function mapCalendarApiItemsToEvents(items: CalendarItemApi[]): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  for (const item of items) {
    if (isBirthdayItem(item)) {
      events.push(mapBirthdayItem(item));
      continue;
    }
    if (isTaskItem(item)) {
      events.push(mapTaskItem(item));
      continue;
    }
    if (isTaskReminderItem(item)) {
      events.push(mapTaskReminderItem(item));
      continue;
    }
    if (isExternalEventItem(item)) {
      events.push(mapExternalEventItem(item));
    }
  }

  return events;
}

const CALENDAR_API_ITEM_TYPE_VALUES = new Set<string>(Object.values(CALENDAR_API_ITEM_TYPES));

export function isCalendarItemApi(value: unknown): value is CalendarItemApi {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string' && CALENDAR_API_ITEM_TYPE_VALUES.has(String(record.type));
}

export function unwrapCalendarItemsPayload(response: unknown): unknown {
  if (Array.isArray(response)) return response;

  if (typeof response === 'object' && response !== null && 'data' in response) {
    return (response as { data: unknown }).data;
  }

  return response;
}

export function parseCalendarItemsResponse(data: unknown): CalendarItemApi[] {
  const payload = unwrapCalendarItemsPayload(data);
  if (!Array.isArray(payload)) return [];
  return payload.filter(isCalendarItemApi);
}
